import { DndContext, PointerSensor, useDroppable, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useMemo, type PointerEvent as ReactPointerEvent } from 'react';
import { Task, TaskStatus } from '../types/Task';
import { PriorityBadge } from './PriorityBadge';
import { TaskActionButtons } from './TaskActionButtons';
import { formatTaskDateTime } from '../utils/taskDueDate';

interface TaskBoardProps {
  tasks: Task[];
  onDelete: (id: number) => void;
  onStatusChange: (id: number, status: string) => void | Promise<void>;
  onReorder: (id: number, targetStatus: TaskStatus, targetIndex: number) => void | Promise<void>;
  onViewDetails: (task: Task) => void;
  onEdit: (task: Task) => void;
  onOpenContextMenu?: (task: Task, x: number, y: number) => void;
}

const BOARD_COLUMNS: Array<{ status: TaskStatus; label: string }> = [
  { status: TaskStatus.TODO, label: 'TODO' },
  { status: TaskStatus.IN_PROGRESS, label: 'In Progress' },
  { status: TaskStatus.DONE, label: 'Done' },
];

const NON_DRAG_SELECTOR = 'button, input, textarea, select, option, a, [role="button"], [data-no-dnd="true"]';
const DRAG_ROOT_ATTRIBUTE = 'data-drag-root';

function isInteractiveDragTarget(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  const interactiveElement = target.closest(NON_DRAG_SELECTOR);
  return Boolean(interactiveElement && !interactiveElement.hasAttribute(DRAG_ROOT_ATTRIBUTE));
}

function isIgnoredContextMenuTarget(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  const interactiveElement = target.closest(NON_DRAG_SELECTOR);
  return Boolean(interactiveElement && !interactiveElement.hasAttribute(DRAG_ROOT_ATTRIBUTE));
}

class BoardPointerSensor extends PointerSensor {
  static activators = [
    {
      eventName: 'onPointerDown' as const,
      handler: ({ nativeEvent }: ReactPointerEvent) => {
        if (!nativeEvent.isPrimary || nativeEvent.button !== 0) {
          return false;
        }

        return !isInteractiveDragTarget(nativeEvent.target);
      },
    },
  ];
}

function formatTimestamp(value: string | undefined) {
  return formatTaskDateTime(value, 'Not updated yet');
}

function formatLabels(labels: string[] | undefined) {
  if (!labels || labels.length === 0) return 'No labels';
  return labels.join(', ');
}

function getTaskSortTimestamp(task: Task) {
  const primaryTimestamp = task.updatedAt ?? task.createdAt;
  const timestampValue = primaryTimestamp ? Date.parse(primaryTimestamp) : 0;
  return Number.isNaN(timestampValue) ? 0 : timestampValue;
}

function compareBoardTasks(leftTask: Task, rightTask: Task) {
  if (typeof leftTask.sortOrder === 'number' && typeof rightTask.sortOrder === 'number') {
    if (leftTask.sortOrder !== rightTask.sortOrder) {
      return leftTask.sortOrder - rightTask.sortOrder;
    }
  } else if (typeof leftTask.sortOrder === 'number') {
    return -1;
  } else if (typeof rightTask.sortOrder === 'number') {
    return 1;
  }

  return getTaskSortTimestamp(rightTask) - getTaskSortTimestamp(leftTask);
}

interface BoardColumnSectionProps {
  column: { status: TaskStatus; label: string };
  tasks: Task[];
  onDelete: (id: number) => void;
  onStatusChange: (id: number, status: string) => void | Promise<void>;
  onReorder: (id: number, targetStatus: TaskStatus, targetIndex: number) => void | Promise<void>;
  onViewDetails: (task: Task) => void;
  onEdit: (task: Task) => void;
  onOpenContextMenu?: (task: Task, x: number, y: number) => void;
}

interface BoardTaskCardProps {
  task: Task;
  onDelete: (id: number) => void;
  onStatusChange: (id: number, status: string) => void | Promise<void>;
  onViewDetails: (task: Task) => void;
  onEdit: (task: Task) => void;
  onOpenContextMenu?: (task: Task, x: number, y: number) => void;
}

function BoardTaskCard({ task, onDelete, onStatusChange, onViewDetails, onEdit, onOpenContextMenu }: BoardTaskCardProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `task-${task.id}`,
    data: {
      taskId: task.id,
      status: task.status,
      type: 'task',
    },
  });
  const cardStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleBoardCardContextMenu = (event: React.MouseEvent<HTMLElement>) => {
    if (!onOpenContextMenu) {
      return;
    }

    if (isIgnoredContextMenuTarget(event.target)) {
      return;
    }

    event.preventDefault();
    onOpenContextMenu(task, event.clientX, event.clientY);
  };

  const handleBoardCardContextMenuKey = (event: React.KeyboardEvent<HTMLElement>) => {
    if (!onOpenContextMenu) {
      return;
    }

    const isKeyboardContextMenu = event.key === 'ContextMenu' || (event.shiftKey && event.key === 'F10');

    if (!isKeyboardContextMenu) {
      return;
    }

    event.preventDefault();
    const rect = event.currentTarget.getBoundingClientRect();
    onOpenContextMenu(task, rect.left + Math.min(48, rect.width / 2), rect.top + Math.min(48, rect.height / 2));
  };

  return (
    <article
      ref={setNodeRef}
      style={cardStyle}
      className={`board-task-card${isDragging ? ' is-dragging' : ''}`}
      data-status={task.status}
      data-task-id={task.id}
      data-drag-root="true"
      onContextMenu={handleBoardCardContextMenu}
      onKeyDown={handleBoardCardContextMenuKey}
      {...attributes}
      {...listeners}
    >
      <div className="board-task-header">
        <h3 className="board-task-title">{task.title}</h3>
        <PriorityBadge priority={task.priority} />
      </div>

      {task.description ? (
        <p className="board-task-description">{task.description}</p>
      ) : null}

      <div className="board-task-meta">
        <span className="board-task-meta-item">Labels: {formatLabels(task.labels)}</span>
        <span className="board-task-meta-item">Updated: {formatTimestamp(task.updatedAt)}</span>
      </div>

      <div className="board-task-footer">
        <select
          value={task.status}
          onChange={(event) => void onStatusChange(task.id!, event.target.value)}
          className="status-dropdown"
          aria-label={`Change status for ${task.title}`}
          data-no-dnd="true"
        >
          <option value="TODO">TODO</option>
          <option value="IN_PROGRESS">In Progress</option>
          <option value="DONE">Done</option>
        </select>

        <div data-no-dnd="true">
          <TaskActionButtons
            onViewDetails={() => onViewDetails(task)}
            onEdit={() => onEdit(task)}
            onDelete={() => {
              if (window.confirm('Are you sure you want to delete this task?')) {
                onDelete(task.id!);
              }
            }}
          />
        </div>
      </div>
    </article>
  );
}

function BoardColumnSection({ column, tasks, onDelete, onStatusChange, onViewDetails, onEdit, onOpenContextMenu }: BoardColumnSectionProps) {
  const { isOver, setNodeRef } = useDroppable({
    id: `column-${column.status}`,
    data: {
      status: column.status,
      type: 'column',
    },
  });

  return (
    <section className="board-column" aria-labelledby={`board-column-${column.status}`} data-board-column={column.status}>
      <div className="board-column-header">
        <h2 id={`board-column-${column.status}`} className="board-column-title">
          {column.label}
        </h2>
        <span className="board-column-count" aria-label={`${tasks.length} tasks in ${column.label}`}>
          {tasks.length}
        </span>
      </div>

      <div
        ref={setNodeRef}
        className={`board-column-body${isOver ? ' is-drop-target' : ''}`}
        data-status={column.status}
      >
        {tasks.length === 0 ? (
          <p className="board-empty-state">No tasks in this column.</p>
        ) : (
          <SortableContext items={tasks.map((task) => `task-${task.id}`)} strategy={verticalListSortingStrategy}>
            {tasks.map((task) => (
              <BoardTaskCard
                key={task.id}
                task={task}
                onDelete={onDelete}
                onStatusChange={onStatusChange}
                onViewDetails={onViewDetails}
                onEdit={onEdit}
                onOpenContextMenu={onOpenContextMenu}
              />
            ))}
          </SortableContext>
        )}
      </div>
    </section>
  );
}

export function TaskBoard({ tasks, onDelete, onStatusChange, onReorder, onViewDetails, onEdit, onOpenContextMenu }: TaskBoardProps) {
  const sensors = useSensors(useSensor(BoardPointerSensor, { activationConstraint: { distance: 8 } }));
  const tasksByStatus = useMemo(
    () => Object.fromEntries(
      BOARD_COLUMNS.map((column) => [
        column.status,
        tasks
          .filter((task) => task.status === column.status)
          .sort(compareBoardTasks),
      ])
    ),
    [tasks]
  ) as Record<TaskStatus, Task[]>;

  const handleDragEnd = (event: DragEndEvent) => {
    const taskId = event.active.data.current?.taskId;
    const sourceStatus = event.active.data.current?.status as TaskStatus | undefined;
    const overData = event.over?.data.current;

    if (typeof taskId !== 'number' || !sourceStatus || !event.over) {
      return;
    }

    const sourceIndex = tasksByStatus[sourceStatus].findIndex((task) => task.id === taskId);
    if (sourceIndex < 0) {
      return;
    }

    const destinationStatus = overData?.status as TaskStatus | undefined;
    if (!destinationStatus) {
      return;
    }

    let targetIndex = tasksByStatus[destinationStatus].length;

    if (overData?.type === 'task') {
      const overTaskId = overData.taskId as number | undefined;
      const overIndex = tasksByStatus[destinationStatus].findIndex((task) => task.id === overTaskId);

      if (overIndex < 0) {
        return;
      }

      targetIndex = overIndex;
    }

    if (destinationStatus === sourceStatus) {
      const droppingToColumnEnd = targetIndex === tasksByStatus[sourceStatus].length;
      if (sourceIndex === targetIndex || (droppingToColumnEnd && sourceIndex === tasksByStatus[sourceStatus].length - 1)) {
        return;
      }
    }

    void onReorder(taskId, destinationStatus, targetIndex);
  };

  return (
    <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
      <div className="task-board" aria-label="Task board view">
        {BOARD_COLUMNS.map((column) => (
          <BoardColumnSection
            key={column.status}
            column={column}
            tasks={tasksByStatus[column.status]}
            onDelete={onDelete}
            onStatusChange={onStatusChange}
            onReorder={onReorder}
            onViewDetails={onViewDetails}
            onEdit={onEdit}
            onOpenContextMenu={onOpenContextMenu}
          />
        ))}
      </div>
    </DndContext>
  );
}